import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Box, ButtonBase, Stack, Typography } from "@mui/material";
import { useTranslation } from "i18n-fe";
import { TableSkeleton } from "common-fe";
import { DEFAULT_ASPECT_RATIO } from "common-fe/scoreboard/aspectRatio.js";
import { TILE_POLL_INTERVAL_MS } from "common-fe/scoreboard/previewIntervals.js";
import { useScoreApi, unwrap } from "../api/scoreApi";

// Card #3377 (report TRI-281) -- a live preview of every scoreboard in the show,
// each a link out to Live Control. The preview is the SHARED common-fe ModePreview
// (the same component score-fe's Live Control draws), never a copy.
//
// Load cost: the dashboard must never wait on this. ShowDashboard only mounts the
// section once the stat cards have loaded; the preview code (layouts, fonts, QR
// lib) is a React.lazy chunk fetched only when a tile first renders; each tile then
// polls at the same 10s the Live Control tiles use (TILE_POLL_INTERVAL_MS). A
// failed views read or a failed tile poll renders nothing alarming: the section
// hides, a tile keeps its last good frame or shows Blank (ModePreview's own rule).
const ModePreview = lazy(() => import("common-fe/scoreboard/ModePreview.jsx"));

// Live Control is score-fe's route (a different app and origin), so the tile is a
// plain link out. score-fe's liveControlPath(showId) plus `?view=<id>`, which
// score-fe's useRemoteViews reads to open on THAT scoreboard (an id it does not
// know falls back to the default view). Unset env = no link at all.
// #3630 -- a compact preview tile (was a 240px+ auto-fill column).
const PREVIEW_TILE_WIDTH = 200;

function liveControlHref(scoreFeBaseUrl, showId, viewId) {
  if (!scoreFeBaseUrl) return null;
  const path = `${scoreFeBaseUrl.replace(/\/+$/, "")}/scoreboard/remote-control/remote/show/${encodeURIComponent(showId)}`;
  return viewId ? `${path}?view=${encodeURIComponent(viewId)}` : path;
}

function PreviewTile({ view, showId, year, scoreFeBaseUrl }) {
  const { t } = useTranslation("scoreadmin");
  const href = liveControlHref(scoreFeBaseUrl, showId, view.id);
  const scoreApi = useScoreApi();
  const scoreApiRef = useRef(scoreApi);
  scoreApiRef.current = scoreApi;
  const slug = view.slug || null;
  const name = view.name || t("dashboard.scoreboards.unnamed");

  // Stable across renders (the poll hook restarts on a changed fetcher). A view
  // with no slug is the show's default view: the two-segment route.
  const fetchPayload = useCallback(async () => {
    const board = scoreApiRef.current.scoreboard;
    const res = slug
      ? await board.getPublicScoreboard(showId, year, slug)
      : await board.getPublicScoreboardDefaultView(showId, year);
    return unwrap(res);
  }, [showId, year, slug]);

  const preview = (
    <Suspense fallback={<TableSkeleton rows={2} columns={1} showHeader={false} />}>
      <ModePreview
        bare
        fetchPayload={fetchPayload}
        showId={showId}
        year={year}
        modeName={name}
        aspectRatio={DEFAULT_ASPECT_RATIO}
        intervalMs={TILE_POLL_INTERVAL_MS}
        qrOrigin={scoreFeBaseUrl || ""}
        testId="dashboard-scoreboard-preview"
      />
    </Suspense>
  );

  return (
    <Box data-testid="dashboard-scoreboard-tile" data-view-id={view.id}>
      {href ? (
        <ButtonBase
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("dashboard.scoreboards.openLiveControl", { name })}
          data-testid="dashboard-scoreboard-link"
          sx={{ display: "block", width: "100%", textAlign: "left", borderRadius: 1, overflow: "hidden", border: 1, borderColor: "divider" }}
        >
          {preview}
        </ButtonBase>
      ) : (
        <Box sx={{ borderRadius: 1, overflow: "hidden", border: 1, borderColor: "divider" }}>{preview}</Box>
      )}
      <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }} data-testid="dashboard-scoreboard-name">
        {name}
      </Typography>
    </Box>
  );
}

export default function ScoreboardPreviewSection({ showId, year }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const [views, setViews] = useState(null);
  const scoreFeBaseUrl = import.meta.env.VITE_SCORE_FE_BASE_URL || "";

  useEffect(() => {
    let cancelled = false;
    setViews(null);
    if (!showId) return undefined;
    (async () => {
      try {
        const items = unwrap(await scoreApi.scoreboard.listScoreboardViews(showId));
        if (!cancelled) setViews(Array.isArray(items) ? items : []);
      } catch {
        // A failed read hides the section; the rest of the dashboard is unaffected.
        if (!cancelled) setViews([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // scoreApi identity is stable per render of the provider; keyed on the show only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showId]);

  if (!showId || !year || !views || views.length === 0) return null;

  return (
    <Box data-testid="dashboard-scoreboards" sx={{ flex: "0 0 auto", maxWidth: { md: "50%" } }}>
      {/* #3630 -- a small title sitting right on its tiles, previews about 200px wide. */}
      <Stack direction="row" alignItems="baseline" flexWrap="wrap" columnGap={1} sx={{ mb: 0.5 }}>
        <Typography variant="subtitle1" component="h2" color="text.primary" sx={{ fontWeight: 500 }}>
          {t("dashboard.scoreboards.title")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("dashboard.scoreboards.hint")}
        </Typography>
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fill, ${PREVIEW_TILE_WIDTH}px)`,
          gap: 1.25,
          p: 1.25,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        {views.map((view) => (
          <PreviewTile key={view.id} view={view} showId={showId} year={year} scoreFeBaseUrl={scoreFeBaseUrl} />
        ))}
      </Box>
    </Box>
  );
}
